import { AnimatePresence, motion } from "framer-motion";
import React, { useState, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { useSelector } from "react-redux";
import logo from "../../assets/coldCoffee.png";
import umbrella from "../../assets/umbrella.png";
import { Clock, Locate, LocateIcon, LucideLocate } from "lucide-react";
import { LocationOn } from "@mui/icons-material";
import ModalPago from "./ModalPago";

const stripePromise = loadStripe("tu_clave_publica_de_stripe");

const CheckoutForm = ({ setIsModalOpen, handleModalPago }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cardType, setCardType] = useState(null);

  useEffect(() => {
    if (!elements) return;

    const cardNumberElement = elements.getElement(CardNumberElement);
    if (cardNumberElement) {
      cardNumberElement.on("change", (event) => {
        if (event.brand && event.brand !== "unknown") {
          setCardType(event.brand);
        } else {
          setCardType(null);
        }
      });
    }
  }, [elements]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);

    try {
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: elements.getElement(CardNumberElement),
      });

      if (error) {
        setError(error.message);
        setLoading(false);
      } else {
        console.log("[PaymentMethod]", paymentMethod);
        setIsModalOpen(false); // Cierra el modal después de un pago exitoso
        // Aquí puedes manejar el pago exitoso
      }
    } catch (error) {
      console.error(error);
      setError("Error al procesar el pago");
      setLoading(false);
    }
  };

  const getCardIcon = (brand) => {
    switch (brand) {
      case "visa":
        return "https://img.icons8.com/color/48/000000/visa.png";
      case "mastercard":
        return "https://img.icons8.com/color/48/000000/mastercard.png";
      case "amex":
        return "https://img.icons8.com/color/48/000000/amex.png";
      case "discover":
        return "https://img.icons8.com/color/48/000000/discover.png";
      default:
        return null;
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="mb-6">
        <label className="block text-sm font-medium text-subtitle mb-1">
          Número de tarjeta
        </label>
        <div className="relative">
          <CardNumberElement
            options={{
              style: {
                base: {
                  fontSize: "16px",
                  color: "#424770",
                  "::placeholder": {
                    color: "#aab7c4",
                  },
                },
                invalid: {
                  color: "#9e2146",
                },
              },
            }}
            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {cardType && (
            <img
              src={getCardIcon(cardType)}
              alt={cardType}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 h-6"
            />
          )}
        </div>
      </div>

      <div className="flex flex-col lg:flex-row justify-between mb-6 space-y-6 lg:space-y-0 lg:space-x-6">
        <div className="w-full lg:w-1/2">
          <label className="block text-sm font-medium text-subtitle mb-1">
            Fecha de expiración
          </label>
          <CardExpiryElement
            options={{
              style: {
                base: {
                  fontSize: "16px",
                  color: "#424770",
                  "::placeholder": {
                    color: "#aab7c4",
                  },
                },
                invalid: {
                  color: "#9e2146",
                },
              },
            }}
            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="w-full lg:w-1/2">
          <label className="block text-sm font-medium text-subtitle mb-1">
            CVV
          </label>
          <CardCvcElement
            options={{
              style: {
                base: {
                  fontSize: "16px",
                  color: "#424770",
                  "::placeholder": {
                    color: "#aab7c4",
                  },
                },
                invalid: {
                  color: "#9e2146",
                },
              },
            }}
            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {error && <div className="text-red-500 text-sm mb-4">{error}</div>}
      <button
        type="submit"
        onClick={() => handleModalPago()}
        disabled={loading || !stripe}
        className="w-full bg-mandarin text-white py-3 px-6 rounded-lg hover:bg-mandarin2 transition-colors duration-200"
      >
        {loading ? "Procesando..." : "Pagar"}
      </button>
    </form>
  );
};

export default function ModalPay({ isModalOpen, setIsModalOpen, isSetPagar, setClickPagar }) {
  const { event } = useSelector(state => state.coffee);

  const handleModalPago = () => {
    setIsModalOpen(false);
    isSetPagar(true);
    setClickPagar(true);
  };

  return (
    <AnimatePresence>
      {isModalOpen && (
        <motion.div
          key="modal-background"
          onClick={() => setIsModalOpen(false)}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
        >
          <motion.div
            key="modal-content"
            className="bg-white p-6 rounded-xl shadow-2xl max-w-4xl w-full mx-4 relative overflow-y-auto"
            style={{ maxHeight: "90vh" }}
            initial={{ y: -50, opacity: 0, scale: 0.95 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: -50, opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3, ease: "easeOut", delay: 0.1 }}
            onClick={(e) => e.stopPropagation()}
          >
            <img className="absolute w-16 top-1 left-1" src={umbrella} alt="" />
            <div className="flex flex-col lg:flex-row items-center justify-between space-y-8 lg:space-y-0 lg:space-x-8 mt-9">

            <div className="w-full sm:hidden block lg:w-1/2 border-l-0 lg:border-l-2 border-l-mandarin text-mandarin lg:pl-8">
                <div className="flex justify-center items-center mb-5">
                  <img className="w-24 rounded" src={logo} alt="" />
                </div>
                <p className="text-center text-mandarin text-3xl font-semibold">Detalles del Pedido</p>

                <div className="ml-8 items-center mt-6 space-y-4">
                  <p className="text-xl  rounded-lg text-black text-center">
                    Tipo de evento: <span className="text-white bg-barbados rounded p-1 ">{event.tipo}</span>
                  </p>
                  <p className="text-xl flex items-center">
                    <LocationOn />
                    Lugar: <span className="text-black ml-1">{event.lugar}</span>
                  </p>
                  <p className="text-xl flex items-center">
                    <Clock className="w-5 h-5 mr-1" />
                    Hora: <span className="text-black ml-1">{event.hora}</span>
                  </p>
                  <p className="text-3xl text-center mr-3 underline underline-offset-4">
                    Total: <span className="text-black">${event.price}</span>
                  </p>
                </div>
              </div>

              <div className="w-full lg:w-1/2 sm:border-t-0 border-t-2 border-t-mandarin pt-5  ">
                <h2 className="text-2xl font-bold mb-6 text-center">Pago con Tarjeta</h2>
                <Elements stripe={stripePromise}>
                  <CheckoutForm setIsModalOpen={setIsModalOpen} handleModalPago={handleModalPago} />
                </Elements>
              </div>

              <div className="w-full sm:block hidden lg:w-1/2 border-l-0 lg:border-l-2 border-l-mandarin text-mandarin lg:pl-8">
                <div className="flex justify-center items-center mb-5">
                  <img className="w-24 rounded" src={logo} alt="" />
                </div>
                <p className="text-center text-mandarin text-3xl font-semibold">Detalles del Pedido</p>

                <div className="ml-8 items-center mt-6 space-y-4">
                  <p className="text-xl p-1 rounded-xl text-black text-center">
                    Tipo de evento: <span className="text-white rounded-lg bg-barbados p-1 px-4">{event.tipo}</span>
                  </p>
                  <p className="text-xl flex items-center">
                    <LocationOn />
                    Lugar: <span className="text-black">{event.lugar}</span>
                  </p>
                  <p className="text-xl flex items-center">
                    <Clock className="w-5 h-5 mr-1" />
                    Hora: <span className="text-black">{event.hora}</span>
                  </p>
                  <p className="text-3xl text-center mr-3 underline underline-offset-4">
                    Total: <span className="text-black">${event.price}</span>
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}